import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '../ds';
import { cx, focusRing, motion } from '../ds/classes';
import { NewProjectDialog } from './ProjectDialogs';
import { ErrorNote } from './ErrorNote';
import { StudioBar, StudioMark, StudioDivider } from '../studio/StudioBar';
import { useStudioReady } from '../studio/studioEntrance';
import { useAuth } from '../../context/AuthContext';
import { useCredits } from '../../hooks/useCredits';
import { track } from '../../utils/analytics';

// The API's times are UTC without a zone ("2026-10-20T18:00:00"); read them as UTC, not local time
const serverTime = (value) => {
  if (!value) return 0;
  const zoned = /([zZ]|[+-]\d\d:?\d\d)$/.test(value) ? value : `${value}Z`;
  return Date.parse(zoned) || 0;
};

const formatDate = (time) => new Date(time).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

const created = (project) => serverTime(project.created_at);
// When it was last worked on: its newest upload. An API without it falls back to when it was made.
const workedOn = (project) => serverTime(project.last_worked_at) || created(project);

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/** "Worked on 2 hours ago", "Worked on yesterday", or a date once it's more than a week back */
export const workedOnText = (time, now = Date.now()) => {
  const minutes = Math.round((now - time) / 60000);
  if (minutes < 1) return 'Worked on just now';
  if (minutes < 60) return `Worked on ${relative.format(-minutes, 'minute')}`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Worked on ${relative.format(-hours, 'hour')}`;
  const days = Math.round(hours / 24);
  if (days < 7) return `Worked on ${relative.format(-days, 'day')}`;
  return `Worked on ${formatDate(time)}`;
};

// How the cards can be ordered
const SORTS = {
  recent: { label: 'Recently worked on', compare: (a, b) => workedOn(b) - workedOn(a) || b.id - a.id },
  newest: { label: 'Newest first', compare: (a, b) => created(b) - created(a) || b.id - a.id },
  oldest: { label: 'Oldest first', compare: (a, b) => created(a) - created(b) || a.id - b.id },
  name: { label: 'Name', compare: (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) },
};

// The sort is a per-browser convenience; anything unreadable falls back to recently worked on
const SORT_KEY = 'tpf-projects-sort';
const savedSort = () => {
  try {
    const saved = localStorage.getItem(SORT_KEY);
    return SORTS[saved] ? saved : 'recent';
  } catch {
    return 'recent';
  }
};

// The sort select: an input's look (base ground, control border), sized to its options
const sortClass = cx('min-h-target-min pl-space-3 pr-space-8 rounded-radius-md bg-base border border-control text-body text-ink', focusRing);

// Cards and the new-project tile share a height, so the grid's rows line up
const cardBase = cx('min-h-[232px] h-full flex flex-col rounded-radius-lg no-underline transition-colors', motion, focusRing);

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/**
 * The stems' latest scores as a row of level-meter bars, like the logo's: taller is better, amber
 * like the score ring. A stem from before scores existed gets a short `line` bar.
 */
const StemMeters = ({ stems }) => (
  <span
    role="img"
    aria-label={`Stem health: ${stems.map((stem) => `${stem.name} ${stem.score ?? 'not scored'}`).join(', ')}`}
    className="flex-1 min-w-0 flex flex-col gap-space-2"
  >
    <span className="h-11 flex items-end gap-1.5" aria-hidden="true">
      {stems.map((stem) => (
        <span
          key={stem.id}
          className={cx('w-3 flex-none rounded-[4px]', stem.score != null ? 'bg-amber' : 'bg-line')}
          style={{ height: `${stem.score != null ? Math.max(stem.score, 1) * 4.4 : 4}px` }}
        />
      ))}
    </span>
    <span className="truncate text-caption text-muted" aria-hidden="true">{stems.map((stem) => stem.name).join(', ')}</span>
  </span>
);

/** The full mix's latest score, or that there isn't one yet */
const MixScore = ({ mix }) => (
  <span className="flex-none flex flex-col items-end gap-0.5 px-space-4 py-space-3 rounded-radius-md bg-base">
    <span className="font-mono text-data-lg text-ink">{mix?.score != null ? `${mix.score}/10` : '–'}</span>
    <span className="text-caption text-muted">{mix ? 'Mastering readiness' : 'No full mix yet'}</span>
  </span>
);

/**
 * One project: its name and DAW, its stems' scores as meters beside the full mix's score, and when
 * it was last worked on. An API that doesn't list clips yet gets the clip count instead.
 */
const ProjectCard = ({ project }) => {
  const clips = project.clips;
  const stems = clips?.filter((clip) => clip.kind !== 'mix') || [];
  const mix = clips?.find((clip) => clip.kind === 'mix');
  const count = clips?.length ?? project.clip_count ?? 0;
  const meta = [
    project.daw,
    clips && stems.length > 0 && plural(stems.length, 'stem', 'stems'),
    clips && mix && 'full mix',
    !clips && count > 0 && plural(count, 'clip', 'clips'),
  ].filter(Boolean).join(' · ');

  return (
    <li>
      <Link to={`/projects/${project.id}`} className={cx(cardBase, 'gap-space-5 p-space-6 bg-surface hover:bg-raised')}>
        <span className="flex flex-col gap-space-1 min-w-0">
          <span className="font-display text-section text-ink break-words">{project.name}</span>
          {meta && <span className="text-caption text-muted">{meta}</span>}
        </span>
        {count === 0 ? (
          <span className="flex-1 flex items-end text-small text-ink-soft">No clips yet. Add a stem or your full mix to get a score.</span>
        ) : clips ? (
          <span className="flex-1 flex items-end gap-space-5">
            {stems.length > 0 ? <StemMeters stems={stems} /> : <span className="flex-1 text-small text-ink-soft">No stems yet</span>}
            <MixScore mix={mix} />
          </span>
        ) : (
          <span className="flex-1" />
        )}
        <span className="pt-space-4 border-t border-raised text-caption text-muted">
          {project.last_worked_at ? workedOnText(workedOn(project)) : project.created_at && `Started ${formatDate(created(project))}`}
        </span>
      </Link>
    </li>
  );
};

/** The last tile in the grid: start a project */
const NewProjectTile = ({ onClick }) => (
  <li>
    <button
      type="button"
      onClick={onClick}
      className={cx(cardBase, 'w-full items-center justify-center gap-space-3 p-space-6 bg-transparent border-2 border-dashed border-line text-amber font-text text-body font-bold hover:bg-surface')}
    >
      <span className="w-target-min h-target-min rounded-full bg-raised inline-flex items-center justify-center">
        <Plus size={20} strokeWidth={2.5} aria-hidden="true" />
      </span>
      Start a new project
    </button>
  </li>
);

/**
 * /projects — Projects home, where the studio opens: a grid of the user's projects, sortable,
 * ending in a tile to start a new one. Renaming and deleting are in each project's own menu.
 */
export const ProjectsPage = () => {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();
  const { creditsRemaining } = useCredits();
  const [projects, setProjects] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [sort, setSort] = useState(savedSort);
  const sortId = useId();

  const load = useCallback(async () => {
    try {
      setProjects(await authFetch('/projects'));
    } catch (err) {
      setError(err.message);
    }
  }, [authFetch]);

  useEffect(() => {
    load();
  }, [load]);

  useStudioReady(projects !== null || Boolean(error));

  const sorted = useMemo(() => (projects ? [...projects].sort(SORTS[sort].compare) : null), [projects, sort]);

  const chooseSort = (next) => {
    setSort(next);
    try {
      localStorage.setItem(SORT_KEY, next);
    } catch {
      // Not remembered when storage is blocked; the choice still applies now
    }
  };

  return (
    <>
      <StudioBar creditsRemaining={creditsRemaining}>
        <StudioMark withName />
        <StudioDivider />
        <span aria-current="page" className="min-w-0 truncate px-space-2 font-display text-project text-ink">Projects</span>
      </StudioBar>

      <main className="flex-1 bg-base text-ink font-text pt-space-10 pb-space-14 px-space-4 sm:px-space-6 lg:px-space-8">
        <div className="w-full max-w-[1360px] mx-auto flex flex-col gap-space-8">
          <header className="flex flex-wrap items-end justify-between gap-space-4">
            <div className="flex flex-col gap-space-2">
              <h1 className="m-0 font-display text-title-1 text-ink">Your projects</h1>
              <p className="m-0 text-body text-ink-soft">
                {projects?.length === 0
                  ? "No projects yet. Start one for the song you're working on, then add its stems or the full mix."
                  : 'Pick up where you left off. Each project holds its stems and full mix.'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-space-3">
              {projects?.length > 1 && (
                <>
                  <label htmlFor={sortId} className="text-label text-muted">Sort</label>
                  <select id={sortId} value={sort} onChange={(event) => chooseSort(event.target.value)} className={sortClass}>
                    {Object.entries(SORTS).map(([value, { label }]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </>
              )}
              <Button icon={Plus} onClick={() => setCreating(true)}>New project</Button>
            </div>
          </header>

          {error && <ErrorNote onDismiss={() => setError(null)}>{error}</ErrorNote>}
          {projects === null && !error && <p role="status" className="m-0 text-small text-muted">Loading your projects…</p>}
          {sorted && (
            <ul aria-label="Projects" className="m-0 p-0 list-none grid gap-space-4 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
              {sorted.map((project) => <ProjectCard key={project.id} project={project} />)}
              <NewProjectTile onClick={() => setCreating(true)} />
            </ul>
          )}
        </div>

        <NewProjectDialog
          open={creating}
          defaultDaw={user?.preferred_daw}
          onClose={() => setCreating(false)}
          onCreate={async (fields) => {
            const project = await authFetch('/projects', { method: 'POST', json: fields });
            track('project_created');
            setCreating(false);
            navigate(`/projects/${project.id}`);
          }}
        />
      </main>
    </>
  );
};
